import { AlertCircle, ArrowLeft, CheckCircle2, CreditCard, LoaderCircle, PackageCheck, RefreshCw, RotateCcw, Truck, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import StatusPill from "../components/StatusPill";
import { OrderActivityPanel, OrderCustomerPanel, OrderDeliveryPanel, OrderItemsPanel, OrderNotesPanel, OrderPricingPanel } from "../orders/OrderDetailPanels";
import OrderProgressTracker from "../orders/OrderProgressTracker";
import OrderTransitionModal from "../orders/OrderTransitionModal";
import { DELIVERY_STATUS_META, FULFILLMENT_STATUS_META, ORDER_STATUS_META, PAYMENT_STATUS_META, PRIORITY_META, meta } from "../orders/orderStatusMeta";
import { cancelOrder, confirmOrder, createOrderNote, fetchOrder, markOrderBaking, markOrderDecorating, markOrderDelivered, markOrderOutForDelivery, markOrderPackaging, markOrderPaid, markOrderReady, refundOrder, scheduleOrderDelivery, startOrderPreparation, updateOrder } from "../orders/ordersApi";

const actionMap = {
  confirm: { key: "confirm", label: "Confirm order", icon: CheckCircle2, needsNote: true, description: "Move this order from review into confirmed status." },
  start: { key: "start", label: "Start preparation", icon: PackageCheck },
  baking: { key: "baking", label: "Mark baking", icon: PackageCheck },
  decorating: { key: "decorating", label: "Mark decorating", icon: PackageCheck },
  packaging: { key: "packaging", label: "Mark packaging", icon: PackageCheck },
  ready: { key: "ready", label: "Mark ready", icon: PackageCheck },
  paid: { key: "paid", label: "Mark paid", icon: CreditCard, needsNote: true },
  out: { key: "out", label: "Out for delivery", icon: Truck },
  delivered: { key: "delivered", label: "Mark delivered", icon: Truck, needsAllowUnpaid: true },
  refund: { key: "refund", label: "Record refund", icon: RotateCcw, needsRefund: true, danger: true },
  cancel: { key: "cancel", label: "Cancel order", icon: XCircle, needsNote: true, danger: true },
};

export default function AdminOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeAction, setActiveAction] = useState(null);
  const [note, setNote] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [requestedDate, setRequestedDate] = useState("");
  const [requestedTime, setRequestedTime] = useState("");

  const loadOrder = useCallback(async (signal) => {
    setIsLoading(true);
    setError("");
    try { setOrder(await fetchOrder(orderId, { signal })); }
    catch (requestError) { if (requestError.name !== "AbortError") setError(requestError.message || "Order could not be loaded."); }
    finally { if (!signal?.aborted) setIsLoading(false); }
  }, [orderId]);

  useEffect(() => {
    const controller = new AbortController();
    loadOrder(controller.signal);
    return () => controller.abort();
  }, [loadOrder]);

  useEffect(() => {
    if (!order) return;
    setRequestedDate(order.requested_delivery_date || "");
    setRequestedTime((order.requested_delivery_window || "").slice(0, 5));
  }, [order]);

  const actions = useMemo(() => {
    if (!order) return [];
    const rows = [];
    if (order.status === "pending_review" || order.status === "draft") rows.push(actionMap.confirm);
    if (["confirmed", "pending_review"].includes(order.status) && order.fulfillment_status === "not_started") rows.push(actionMap.start);
    if (["preparing", "scheduled", "ingredients_needed"].includes(order.fulfillment_status)) rows.push(actionMap.baking);
    if (order.fulfillment_status === "baking") rows.push(actionMap.decorating);
    if (order.fulfillment_status === "decorating") rows.push(actionMap.packaging);
    if (["packaging", "preparing", "baking", "decorating"].includes(order.fulfillment_status)) rows.push(actionMap.ready);
    if (order.payment_status !== "paid" && order.payment_status !== "refunded") rows.push(actionMap.paid);
    if (order.delivery_status === "ready_for_delivery") rows.push(actionMap.out);
    if (order.delivery_status === "out_for_delivery") rows.push(actionMap.delivered);
    if (Number(order.amount_paid || 0) > Number(order.amount_refunded || 0)) rows.push(actionMap.refund);
    if (!["completed", "canceled", "refunded"].includes(order.status)) rows.push(actionMap.cancel);
    return rows;
  }, [order]);

  async function runAction(action, payload = {}) {
    setIsSaving(true);
    setError("");
    try {
      const id = order.id;
      const saved = await ({
        confirm: () => confirmOrder(id, payload),
        start: () => startOrderPreparation(id),
        baking: () => markOrderBaking(id),
        decorating: () => markOrderDecorating(id),
        packaging: () => markOrderPackaging(id),
        ready: () => markOrderReady(id),
        paid: () => markOrderPaid(id, payload),
        out: () => markOrderOutForDelivery(id),
        delivered: () => markOrderDelivered(id, payload),
        refund: () => refundOrder(id, payload),
        cancel: () => cancelOrder(id, payload),
      })[action.key]();
      setOrder(saved);
      setActiveAction(null);
      setSuccess(`${action.label} complete.`);
    } catch (requestError) {
      setError(requestError.message || "Order action could not be completed.");
    } finally {
      setIsSaving(false);
    }
  }

  async function addNote(value) {
    if (!value.trim()) return;
    setIsSaving(true);
    try {
      await createOrderNote(order.id, { note: value.trim(), is_pinned: false });
      setNote("");
      setOrder(await fetchOrder(order.id));
    } catch (requestError) {
      setError(requestError.message || "Order note could not be saved.");
    } finally {
      setIsSaving(false);
    }
  }

  async function scheduleDelivery(value) {
    setIsSaving(true);
    try {
      const scheduled_delivery_at = value ? new Date(value).toISOString() : null;
      setOrder(await scheduleOrderDelivery(order.id, { scheduled_delivery_at }));
      setSuccess("Delivery scheduled.");
    } catch (requestError) {
      setError(requestError.message || "Delivery could not be scheduled.");
    } finally {
      setIsSaving(false);
    }
  }


  async function updateRequestedDeliverySchedule(dateValue, timeValue) {
    setIsSaving(true);
    setError("");
    try {
      const saved = await updateOrder(order.id, {
        requested_delivery_date: dateValue,
        requested_delivery_window: timeValue,
      });
      await createOrderNote(order.id, { note: `Requested delivery changed to ${dateValue} ${timeValue}.`, is_pinned: false });
      setOrder(await fetchOrder(saved.id));
      setSuccess("Requested delivery updated.");
    } catch (requestError) {
      setError(requestError.message || "Requested delivery could not be updated.");
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) return <AdminLayout><div className="inventory-loading"><LoaderCircle size={22} className="admin-spin" /> Loading order...</div></AdminLayout>;
  if (!order) return <AdminLayout><section className="admin-page"><div className="admin-menu-error"><AlertCircle size={18} /><span>{error || "Order not found."}</span></div></section></AdminLayout>;

  const statuses = [
    [ORDER_STATUS_META, order.status, order.status_label, "status"],
    [PAYMENT_STATUS_META, order.payment_status, order.payment_status_label, "payment_status"],
    [FULFILLMENT_STATUS_META, order.fulfillment_status, order.fulfillment_status_label, "fulfillment_status"],
    [DELIVERY_STATUS_META, order.delivery_status, order.delivery_status_label, "delivery_status"],
    [PRIORITY_META, order.priority, order.priority_label, "priority"],
  ];

  return (
    <AdminLayout>
      <section className="admin-page order-detail-page">
        <header className="admin-page-heading order-detail-heading">
          <div>
            <button type="button" className="btn btn--secondary" onClick={() => navigate("/admin/orders")}><ArrowLeft size={16} /> Orders</button>
            <span className="admin-eyebrow">Order</span>
            <h1>{order.order_number}</h1>
            <p>{order.customer_name_snapshot || "Guest customer"} - {order.customer_email_snapshot || "No email"}</p>
          </div>
          <div className="order-heading-actions">
            <button type="button" className="btn btn--secondary" onClick={() => loadOrder()}><RefreshCw size={16} /> Refresh</button>
          </div>
        </header>

        {error && <div className="admin-menu-error"><AlertCircle size={18} /><span>{error}</span></div>}
        {success && <div className="inventory-success">{success}</div>}

        <div className="order-status-strip">
          {statuses.map(([map, status, label, key]) => {
            const row = meta(map, status, label);
            return <StatusPill key={key} status={status} label={row.label} type={order.status_tones?.[key] || row.tone} />;
          })}
        </div>

        <div className="order-action-bar">
          {actions.map((action) => {
            const Icon = action.icon;
            const immediate = !action.needsNote && !action.needsRefund && !action.needsAllowUnpaid;
            return <button type="button" key={action.key} className={action.danger ? "btn admin-danger-button" : "btn btn--secondary"} disabled={isSaving} onClick={() => immediate ? runAction(action) : setActiveAction(action)}><Icon size={16} /> {action.label}</button>;
          })}
        </div>

        <OrderProgressTracker order={order} />
        <div className="order-detail-grid">
          <OrderItemsPanel order={order} />
          <OrderPricingPanel order={order} />
          <OrderCustomerPanel order={order} />
          <OrderDeliveryPanel order={order} scheduleAt={scheduleAt} setScheduleAt={setScheduleAt} requestedDate={requestedDate} setRequestedDate={setRequestedDate} requestedTime={requestedTime} setRequestedTime={setRequestedTime} isSaving={isSaving} onSchedule={scheduleDelivery} onUpdateRequested={updateRequestedDeliverySchedule} />
          <OrderNotesPanel order={order} note={note} setNote={setNote} isSaving={isSaving} onAddNote={addNote} />
          <OrderActivityPanel order={order} />
        </div>
      </section>
      <OrderTransitionModal action={activeAction} isSaving={isSaving} onClose={() => !isSaving && setActiveAction(null)} onSubmit={runAction} />
    </AdminLayout>
  );
}
